/**
 * Meta Pixel (Facebook Pixel) Service
 *
 * Gère l'initialisation et le tracking des événements Meta Pixel.
 * Respecte le RGPD : le pixel n'est chargé qu'après consentement marketing.
 */

// Déclaration du type global pour fbq
declare global {
  interface Window {
    fbq: (...args: unknown[]) => void;
    _fbq: unknown;
  }
}

// État du pixel
let isPixelInitialized = false;

/**
 * Obtient l'ID du Meta Pixel depuis les variables d'environnement
 */
const getPixelId = (): string | undefined => {
  return import.meta.env.VITE_META_PIXEL_ID;
};

/**
 * Vérifie si le pixel est initialisé et prêt à tracker
 */
export const isMetaPixelReady = (): boolean => {
  return isPixelInitialized && typeof window !== "undefined" && typeof window.fbq === "function";
};

/**
 * Initialise le Meta Pixel
 * Cette fonction charge le script Facebook et initialise le pixel avec l'ID configuré.
 * Elle doit être appelée uniquement après le consentement marketing de l'utilisateur.
 */
export const initMetaPixel = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    const pixelId = getPixelId();

    if (!pixelId) {
      if (import.meta.env.DEV) {
        console.warn("[Meta Pixel] VITE_META_PIXEL_ID non configuré");
      }
      resolve();
      return;
    }

    if (typeof window === "undefined") {
      resolve();
      return;
    }

    // Éviter la double initialisation
    if (isPixelInitialized || window.fbq) {
      resolve();
      return;
    }

    try {
      // Initialisation du fbq avant le chargement du script
      const fbq = function (...args: unknown[]) {
        if ((fbq as unknown as { callMethod?: (...a: unknown[]) => void }).callMethod) {
          (fbq as unknown as { callMethod: (...a: unknown[]) => void }).callMethod(...args);
        } else {
          (fbq as unknown as { queue: unknown[] }).queue.push(args);
        }
      };

      if (!window._fbq) window._fbq = fbq;
      (fbq as unknown as { push: typeof Array.prototype.push }).push = fbq as unknown as typeof Array.prototype.push;
      (fbq as unknown as { loaded: boolean }).loaded = true;
      (fbq as unknown as { version: string }).version = "2.0";
      (fbq as unknown as { queue: unknown[] }).queue = [];

      window.fbq = fbq;

      // Charger le script Facebook
      const script = document.createElement("script");
      script.async = true;
      script.src = "https://connect.facebook.net/en_US/fbevents.js";

      script.onload = () => {
        // Initialiser le pixel avec l'ID
        window.fbq("init", pixelId);
        isPixelInitialized = true;

        if (import.meta.env.DEV) {
          console.log("[Meta Pixel] Initialisé avec succès");
        }

        resolve();
      };

      script.onerror = () => {
        if (import.meta.env.DEV) {
          console.error("[Meta Pixel] Erreur de chargement du script");
        }
        reject(new Error("Erreur de chargement du Meta Pixel"));
      };

      // Insérer le script dans le DOM
      const firstScript = document.getElementsByTagName("script")[0];
      if (firstScript && firstScript.parentNode) {
        firstScript.parentNode.insertBefore(script, firstScript);
      } else {
        document.head.appendChild(script);
      }
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error("[Meta Pixel] Erreur d'initialisation:", error);
      }
      reject(error);
    }
  });
};

/**
 * Track un événement PageView
 * Appelé automatiquement lors des navigations SPA
 */
export const trackPageView = (): void => {
  if (!isMetaPixelReady()) {
    return;
  }

  try {
    window.fbq("track", "PageView");

    if (import.meta.env.DEV) {
      console.log("[Meta Pixel] PageView tracké");
    }
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error("[Meta Pixel] Erreur PageView:", error);
    }
  }
};

/**
 * Track un événement d'achat/réservation confirmée
 * @param value - Montant de la transaction
 * @param currency - Devise (par défaut MAD)
 * @param contentName - Nom du contenu (optionnel)
 * @param contentId - ID du contenu (optionnel)
 */
export const trackPurchase = (
  value: number,
  currency: string = "MAD",
  contentName?: string,
  contentId?: string
): void => {
  if (!isMetaPixelReady()) {
    return;
  }

  try {
    const eventData: Record<string, unknown> = {
      value,
      currency,
    };

    if (contentName) {
      eventData.content_name = contentName;
    }

    if (contentId) {
      eventData.content_ids = [contentId];
      eventData.content_type = "product";
    }

    window.fbq("track", "Purchase", eventData);

    if (import.meta.env.DEV) {
      console.log("[Meta Pixel] Purchase tracké:", eventData);
    }
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error("[Meta Pixel] Erreur Purchase:", error);
    }
  }
};

/**
 * Track un événement InitiateCheckout (début de réservation)
 * @param value - Montant estimé
 * @param currency - Devise (par défaut MAD)
 */
export const trackInitiateCheckout = (value?: number, currency: string = "MAD"): void => {
  if (!isMetaPixelReady()) {
    return;
  }

  try {
    const eventData: Record<string, unknown> = {};

    if (value !== undefined) {
      eventData.value = value;
      eventData.currency = currency;
    }

    window.fbq("track", "InitiateCheckout", eventData);

    if (import.meta.env.DEV) {
      console.log("[Meta Pixel] InitiateCheckout tracké:", eventData);
    }
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error("[Meta Pixel] Erreur InitiateCheckout:", error);
    }
  }
};

/**
 * Track un événement ViewContent (vue d'un véhicule)
 * @param contentName - Nom du véhicule
 * @param contentId - ID du véhicule
 * @param value - Prix journalier (optionnel)
 * @param currency - Devise (par défaut MAD)
 */
export const trackViewContent = (
  contentName: string,
  contentId: string,
  value?: number,
  currency: string = "MAD"
): void => {
  if (!isMetaPixelReady()) {
    return;
  }

  try {
    const eventData: Record<string, unknown> = {
      content_name: contentName,
      content_ids: [contentId],
      content_type: "product",
    };

    if (value !== undefined) {
      eventData.value = value;
      eventData.currency = currency;
    }

    window.fbq("track", "ViewContent", eventData);

    if (import.meta.env.DEV) {
      console.log("[Meta Pixel] ViewContent tracké:", eventData);
    }
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error("[Meta Pixel] Erreur ViewContent:", error);
    }
  }
};

/**
 * Track un événement Lead (inscription, création de compte)
 */
export const trackLead = (): void => {
  if (!isMetaPixelReady()) {
    return;
  }

  try {
    window.fbq("track", "Lead");

    if (import.meta.env.DEV) {
      console.log("[Meta Pixel] Lead tracké");
    }
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error("[Meta Pixel] Erreur Lead:", error);
    }
  }
};

/**
 * Track un événement personnalisé
 * @param eventName - Nom de l'événement
 * @param eventData - Données de l'événement (optionnel)
 */
export const trackCustomEvent = (eventName: string, eventData?: Record<string, unknown>): void => {
  if (!isMetaPixelReady()) {
    return;
  }

  try {
    if (eventData) {
      window.fbq("trackCustom", eventName, eventData);
    } else {
      window.fbq("trackCustom", eventName);
    }

    if (import.meta.env.DEV) {
      console.log(`[Meta Pixel] Custom event "${eventName}" tracké:`, eventData);
    }
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error(`[Meta Pixel] Erreur custom event "${eventName}":`, error);
    }
  }
};

